import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

export const AnimatedCounter = ({ value, duration = 1000 }) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let startTime;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * value));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [value, duration]);
  return <span>{count}</span>;
};

export const MiniPieChart = ({ good, attention, critical, size = 80 }) => {
  const total = good + attention + critical;
  if (total === 0) return null;
  const goodPercent = (good / total) * 100;
  const attentionPercent = (attention / total) * 100;
  const circumference = 2 * Math.PI * 30;
  const goodDash = (goodPercent / 100) * circumference;
  const attentionDash = (attentionPercent / 100) * circumference;
  const criticalDash = circumference - goodDash - attentionDash;
  const goodRotation = -90;
  const attentionRotation = goodRotation + (goodPercent / 100) * 360;
  const criticalRotation = attentionRotation + (attentionPercent / 100) * 360;
  return (
    <svg width={size} height={size} viewBox="0 0 80 80">
      {critical > 0 && <circle cx="40" cy="40" r="30" fill="transparent" stroke="#ef4444" strokeWidth="12" strokeDasharray={`${criticalDash} ${circumference}`} transform={`rotate(${criticalRotation} 40 40)`} />}
      {attention > 0 && <circle cx="40" cy="40" r="30" fill="transparent" stroke="#eab308" strokeWidth="12" strokeDasharray={`${attentionDash} ${circumference}`} transform={`rotate(${attentionRotation} 40 40)`} />}
      {good > 0 && <circle cx="40" cy="40" r="30" fill="transparent" stroke="#22c55e" strokeWidth="12" strokeDasharray={`${goodDash} ${circumference}`} transform={`rotate(${goodRotation} 40 40)`} />}
      <circle cx="40" cy="40" r="20" fill="white" />
    </svg>
  );
};

export const Speedometer = ({ percentage, size = 180 }) => {
  const getStatus = () => {
    if (percentage === 0) return { label: 'Not Started', color: '#94a3b8' };
    if (percentage <= 33) return { label: 'Initiated', color: '#f97316' };
    if (percentage <= 66) return { label: 'Half Done', color: '#eab308' };
    return { label: 'Completed', color: '#22c55e' };
  };
  const status = getStatus();
  const needleRotation = -90 + (percentage * 1.8);
  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size * 0.65} viewBox="0 0 200 130">
        <path d="M 20 100 A 80 80 0 0 1 80 27" fill="none" stroke="#f97316" strokeWidth="20" strokeLinecap="round" opacity={percentage <= 33 ? 1 : 0.3} />
        <path d="M 80 27 A 80 80 0 0 1 120 27" fill="none" stroke="#eab308" strokeWidth="20" strokeLinecap="round" opacity={percentage > 33 && percentage <= 66 ? 1 : 0.3} />
        <path d="M 120 27 A 80 80 0 0 1 180 100" fill="none" stroke="#22c55e" strokeWidth="20" strokeLinecap="round" opacity={percentage > 66 ? 1 : 0.3} />
        <g transform={`rotate(${needleRotation} 100 100)`}>
          <line x1="100" y1="100" x2="100" y2="35" stroke="#1e293b" strokeWidth="4" strokeLinecap="round" />
          <circle cx="100" cy="100" r="8" fill="#1e293b" />
        </g>
        <circle cx="100" cy="100" r="12" fill="white" stroke="#e2e8f0" strokeWidth="2" />
        <text x="20" y="125" fill="#f97316" fontSize="10" fontWeight="500">Initiated</text>
        <text x="80" y="15" fill="#eab308" fontSize="10" fontWeight="500" textAnchor="middle">Half Done</text>
        <text x="155" y="125" fill="#22c55e" fontSize="10" fontWeight="500">Completed</text>
      </svg>
      <div className="mt-2 text-center">
        <span className="text-lg font-bold" style={{ color: status.color }}>{status.label}</span>
      </div>
    </div>
  );
};

export const ProgressBar = ({ percentage, color = 'green' }) => {
  const colorClasses = {
    green: 'bg-gradient-to-r from-green-400 to-green-600',
    yellow: 'bg-gradient-to-r from-yellow-400 to-yellow-600',
    red: 'bg-gradient-to-r from-red-400 to-red-600',
    blue: 'bg-gradient-to-r from-blue-400 to-blue-600'
  };
  return (
    <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
      <motion.div className={`h-full ${colorClasses[color]}`} initial={{ width: 0 }} animate={{ width: `${percentage}%` }} transition={{ duration: 1, ease: "easeOut" }} />
    </div>
  );
};

export const HorizontalBarChart = ({ data }) => {
  return (
    <div className="space-y-4">
      {data.map((item, index) => (
        <div key={index}>
          <div className="flex justify-between mb-1">
            <span className="text-sm font-medium text-slate-700">{item.label}</span>
            <span className="text-sm text-slate-500">{item.value}%</span>
          </div>
          <div className="h-4 bg-slate-100 rounded-full overflow-hidden">
            <motion.div
              className={`h-full rounded-full ${item.value >= 80 ? 'bg-gradient-to-r from-green-400 to-green-500' : item.value >= 50 ? 'bg-gradient-to-r from-yellow-400 to-yellow-500' : 'bg-gradient-to-r from-red-400 to-red-500'}`}
              initial={{ width: 0 }}
              animate={{ width: `${item.value}%` }}
              transition={{ duration: 1, delay: index * 0.1, ease: "easeOut" }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};
